import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton, styled } from '@mui/material';
import { v4 } from 'uuid';

import { EmptyState } from '@components/feedback';
import useGlobalViewMode from '@hooks/useGlobalViewMode';
import { SimpleTitle } from '@models/Title';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';

import { SearchCardActionProps, SearchResultCard } from './SearchResultCard';

type SearchResultsGridProps = {
  cardActions?: FC<SearchCardActionProps>;
  onSelect?: (simpleTitle: SimpleTitle) => void;
};

const Container = styled('div')`
  display: grid;
  column-gap: 1rem;
  row-gap: 1rem;

  &.wide {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));

    ${({ theme }) => theme.breakpoints.up('sm')} {
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    }
  }

  &.square,
  &.poster {
    grid-template-columns: repeat(auto-fill, 1fr);

    ${({ theme }) => theme.breakpoints.up('sm')} {
      grid-template-columns: repeat(auto-fill, minmax(370px, 1fr));
    }
  }
`;

const StyledSkeleton = styled(Skeleton)`
  transform: scale(1);
  transform-origin: 0 0;
`;

const SearchGrid: FC<SearchResultsGridProps> = ({ cardActions, onSelect }) => {
  const { t } = useTranslation();
  const { imageType } = useGlobalViewMode();

  const {
    queryResult: { isLoading, titles: { data = [] } = {} },
  } = useSearchAndFilter();

  const skeletons: number[] = Array.from(Array(data.length || 12).keys());

  const dataWithIds = useMemo(
    () => data.map(item => ({ ...item, id: v4() })),
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    [data]
  );

  return dataWithIds.length === 0 && !isLoading ? (
    <EmptyState
      circleIcon
      description={t('crud.emptyState.description', { context: 'search' })}
      icon='searchOff'
    />
  ) : (
    <Container className={imageType}>
      {isLoading
        ? skeletons.map(skeleton => <StyledSkeleton height={320} key={skeleton.toString()} />)
        : dataWithIds.map(({ value, id }) => (
            <SearchResultCard actions={cardActions} key={id} onClick={onSelect} title={value} />
          ))}
    </Container>
  );
};

export default SearchGrid;
